'use client';

import { type BillingPeriod, PLANS } from '@/entities/plan';
import { useRegisterWithCheckout } from '@/features/auth-signup';
import { FORGOT_PATHNAME, LOGIN_PATHNAME } from '@/shared/constants/pathnames';
import { Button, Separator, Text } from '@/shared/ui/core';
import { BillingToggle } from '@/shared/ui/kit/billing-toggle';
import { PlanCard } from '@/shared/ui/kit/plan-card';
import { RequiredDataNote } from '@/shared/ui/kit/required-data-note';
import { RegistrationCard, RegistrationCardContent, RegistrationCardDescription, RegistrationCardHeader, RegistrationCardTitle } from '@/shared/ui/layout/registration-card';
import { WarningIcon } from '@phosphor-icons/react/dist/ssr';
import { ArrowRightIcon, RepeatIcon } from '@phosphor-icons/react/ssr';
import { useRef } from 'react';
import { useWizard } from '../../../model/wizard-context';
import { WizardFooter } from '../../wizard-footer';
import { BillingAddressSubForm, type BillingAddressSubFormHandle } from './billing-address-sub-form';
import styles from './step-payment.module.scss';
import Link from 'next/link';


export const StepPayment = () => {
    const { state, dispatch } = useWizard();

    const billingRef = useRef<BillingAddressSubFormHandle>(null);

    const { submit: signup, loading: signupLoading, error: signupError } = useRegisterWithCheckout();

    const selectedPlan  = state.data.plan
        ? PLANS.find(p => p.id === state.data.plan!.plan_id)
        : undefined;
    const billingPeriod = state.data.plan?.billing_period ?? 'monthly';

    const setBillingPeriod = (next: BillingPeriod) => {
        if (!state.data.plan) return;
        dispatch({
            type : 'commit',
            key  : 'plan',
            value: { ...state.data.plan, billing_period: next },
        });
    };

    const handleRegister = async () => {
        // 1. Validate billing, get fresh data
        const billingData = await billingRef.current?.submit();
        if (!billingData) return;

        // 2. Check required wizard data
        if (!state.data.profile || !state.data.company || !state.data.credentials || !state.data.plan) {
            return;
        }

        // 3. Submit to BFF
        const result = await signup({
            billing_address: billingData,
            company        : state.data.company,
            credentials    : state.data.credentials,
            plan           : state.data.plan,
            profile        : state.data.profile,
        });

        if (!result) return;

        // 4. Paid plan → redirect to Chargebee; free/enterprise → next step
        if (result.checkoutUrl) {
            window.location.href = result.checkoutUrl;
            return;
        }

        dispatch({ type: 'next' });
    };

    const handleChangePlan = () => {
        dispatch({ type: 'goto', step: 'plan' });
    };

    return (
        <div>
            <RegistrationCard data-template="registration-steps-card">
                <RegistrationCardHeader data-template="registration-steps-card-header">
                    <RegistrationCardTitle data-template="registration-steps-card-title">
                        Billing information
                    </RegistrationCardTitle>

                    <RegistrationCardDescription data-template="registration-steps-card-description">
                        Please fill out the form to complete registration.
                    </RegistrationCardDescription>
                </RegistrationCardHeader>

                <RegistrationCardContent data-template="registration-steps-card-content">
                    <div data-template="step-payment" className={ styles.root }>
                        {
                            selectedPlan &&
                            <div className={ styles.plan_summary }>
                                <div className={ styles.header_slot }>
                                    <Text
                                        as='h3'
                                        weight='semibold'
                                        lineHeight={ 1.2 }
                                    >
                                        Selected plan
                                    </Text>

                                    <Text
                                        as='div'
                                        size='lg'
                                        tone='secondary'
                                        weight='regular'
                                        lineHeight={ 1.2 }
                                    >
                                        You can change it now or later.
                                    </Text>
                                </div>

                                <PlanCard
                                    plan={ selectedPlan }
                                    billingPeriod={ billingPeriod }
                                    isSelected={ false }
                                    isStandalone
                                    Button={
                                        <Button
                                            leftIcon={ <RepeatIcon/> }
                                            onClick={ handleChangePlan }
                                        >
                                            Change
                                        </Button>
                                    }
                                />

                                <Text
                                    as='h3'
                                    weight='semibold'
                                    lineHeight={ 1.2 }
                                    className={ styles.subheader }
                                >
                                    Billing period
                                </Text>

                                <BillingToggle
                                    value={ billingPeriod }
                                    onChange={ setBillingPeriod }
                                    discountPercent={ 0 }
                                />
                            </div>
                        }

                        {
                            !selectedPlan &&
                            <div className={ styles.plan_summary }/>
                        }

                        <Separator orientation="vertical" className={ styles.separator_left }/>

                        <div className={ styles.billing }>
                            <div className={ styles.header_slot }>
                                <Text
                                    as='h3'
                                    weight='semibold'
                                    lineHeight={ 1.2 }
                                >
                                    Billing information
                                </Text>

                                <Text
                                    as='div'
                                    size='lg'
                                    tone='secondary'
                                    weight='regular'
                                    lineHeight={ 1.2 }
                                >
                                    Enter your details for billing information.
                                </Text>
                            </div>

                            <BillingAddressSubForm
                                ref={ billingRef }
                                stateData={ state.data }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'billing_address', value: data }) }
                            />

                            {
                                signupError?.kind === 'email_exists' &&
                                <div className={ styles.exists_warning }>
                                    <WarningIcon
                                        size={ 20 }
                                        weight="fill"
                                        className={ styles.warning_icon }
                                    />
                                    <Text
                                        size="md"
                                        tone='warning'
                                    >
                                        Looks like you already have an account. Try to <Link href={ `${ LOGIN_PATHNAME }?email=${ encodeURIComponent(state.data.email?.email ?? '') }` } className={ styles.link }>log in</Link> or <Link href={ FORGOT_PATHNAME } className={ styles.link }>reset your password</Link>.
                                    </Text>
                                </div>
                            }

                            {
                                signupError?.kind === 'generic' &&
                                <Text
                                    size="sm"
                                    tone="destructive"
                                    className={ styles.error }
                                >
                                    { signupError.message }
                                </Text>
                            }
                        </div>
                    </div>
                </RegistrationCardContent>
            </RegistrationCard>

            <WizardFooter
                state={ state }
                left={
                    <RequiredDataNote className={ styles.required }/>
                }
                right={
                    <Button
                        leftIcon={ <ArrowRightIcon/> }
                        onClick={ handleRegister }
                        loading={ signupLoading }
                    >
                        Submit
                    </Button>
                }
            />
        </div>
    );
};
